import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import SubscriptionPaymentService from "../../../services/SubscriptionPaymentService";
import UploadAndDownloadService from "../../../services/uploadAndDownloadFile";

/*
 * Super admin: subscription payments declared by the clients and waiting for a decision.
 * Validating a payment renews the contract of the company; refusing it needs a reason that
 * the client sees.
 */
const MODE_LABEL = { VIREMENT: "Virement", CHEQUE: "Chèque", ESPECES: "Espèces", MOBILE: "Paiement mobile" };
const REASON_MAX = 300;

const formatIso = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = String(iso).split("-");
  return `${d}/${m}/${y}`;
};

const formatSent = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 60 * 24) return `il y a ${Math.round(minutes / 60)} h`;
  return date.toLocaleDateString("fr-FR");
};

const ERRORS = {
  forbidden: "Action non autorisée",
  not_found: "Paiement introuvable",
  not_pending: "Ce paiement a déjà été traité",
  reason_required: "Le motif du refus est obligatoire",
  reason_too_long: `Le motif ne doit pas dépasser ${REASON_MAX} caractères`,
  contract_failed: "Le contrat n'a pas pu être renouvelé",
};

const PaiementsAbonnement = () => {
  const [payments, setPayments] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [dialog, setDialog] = useState(null); // { type: "validate" | "refuse", payment }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setState("loading");
    SubscriptionPaymentService.pending()
      .then((response) => {
        if (response.data && Array.isArray(response.data.success)) {
          setPayments(response.data.success);
          setState("ready");
        } else {
          setState("error");
        }
      })
      .catch(() => setState("error"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openProof = (payment) => {
    if (!payment.proofFileUrl) return;
    // the window is opened first, from the click, so the browser does not block it
    const win = window.open("", "_blank");
    UploadAndDownloadService.downloadFile(payment.proofFileUrl)
      .then((response) => {
        const type = response.headers["content-type"] || "application/octet-stream";
        const url = URL.createObjectURL(new Blob([response.data], { type }));
        if (win) win.location.href = url;
      })
      .catch(() => {
        if (win) win.close();
        toast.error("Impossible d'ouvrir le justificatif");
      });
  };

  const close = () => {
    if (busy) return;
    setDialog(null);
    setReason("");
  };

  const confirm = () => {
    if (!dialog || busy) return;
    if (dialog.type === "refuse" && reason.trim() === "") {
      toast.error(ERRORS.reason_required);
      return;
    }
    setBusy(true);
    const call =
      dialog.type === "validate"
        ? SubscriptionPaymentService.validate(dialog.payment.id)
        : SubscriptionPaymentService.refuse(dialog.payment.id, reason.trim());
    call
      .then((response) => {
        if (response.data && response.data.success) {
          toast.success(dialog.type === "validate" ? "Paiement validé, abonnement prolongé" : "Paiement refusé");
          setDialog(null);
          setReason("");
          load();
        } else {
          toast.error(ERRORS[response.data && response.data.error] || "Erreur : action impossible");
          if (response.data && response.data.error === "not_pending") load();
        }
      })
      .catch(() => toast.error("Erreur : problème de connexion"))
      .finally(() => setBusy(false));
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <ToastContainer />
      <section className="content-header">
        <div className="container-fluid">
          <h4 className="mb-1">
            <i className="fas fa-money-check-alt mr-2"></i>Paiements d'abonnement
          </h4>
          <small className="text-muted">Paiements déclarés par les clients, en attente de validation</small>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          <div className="card mt-3">
            <div className="card-body table-responsive p-0">
              {state === "loading" && <div className="p-3 text-muted">Chargement…</div>}
              {state === "error" && (
                <div className="p-3">
                  <span className="text-danger">Impossible de charger les paiements.</span>{" "}
                  <button className="btn btn-sm btn-outline-dark" onClick={load}>
                    Réessayer
                  </button>
                </div>
              )}
              {state === "ready" && payments.length === 0 && (
                <div className="p-3 text-muted">Aucun paiement à valider.</div>
              )}
              {state === "ready" && payments.length > 0 && (
                <table className="table table-hover">
                  <thead className="thead-light">
                    <tr>
                      <th>Société</th>
                      <th>Montant TTC</th>
                      <th>Période payée</th>
                      <th>Mode</th>
                      <th>Référence</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {p.societeTitle || `Société ${p.idSociete}`}
                          <div className="text-muted small">{formatSent(p.creationDate)}</div>
                        </td>
                        <td>{Number(p.amount).toFixed(3)} TND</td>
                        <td>
                          {formatIso(p.periodStart)} - {formatIso(p.periodEnd)}
                        </td>
                        <td>{MODE_LABEL[p.mode] || p.mode}</td>
                        <td>{p.reference || "-"}</td>
                        <td className="text-right text-nowrap">
                          {p.proofFileUrl && (
                            <button className="btn btn-sm btn-outline-dark mr-1" onClick={() => openProof(p)}>
                              <i className="fas fa-paperclip mr-1"></i>Justificatif
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-danger mr-1"
                            onClick={() => setDialog({ type: "refuse", payment: p })}
                          >
                            Refuser
                          </button>
                          <button className="btn btn-sm btn-dark" onClick={() => setDialog({ type: "validate", payment: p })}>
                            Valider
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </section>

      {dialog && (
        <Dialog open onClose={close} fullWidth maxWidth="sm">
          <DialogTitle>{dialog.type === "validate" ? "Valider le paiement" : "Refuser le paiement"}</DialogTitle>
          <DialogContent>
            <p>
              <b>{dialog.payment.societeTitle || `Société ${dialog.payment.idSociete}`}</b> :{" "}
              {Number(dialog.payment.amount).toFixed(3)} TND ({MODE_LABEL[dialog.payment.mode] || dialog.payment.mode}
              {dialog.payment.reference ? `, réf. ${dialog.payment.reference}` : ""})
            </p>
            {dialog.type === "validate" ? (
              <p className="mb-0">
                Le contrat sera renouvelé du {formatIso(dialog.payment.periodStart)} au{" "}
                {formatIso(dialog.payment.periodEnd)} et le client sera réactivé s'il ne l'est pas.
              </p>
            ) : (
              <div className="form-group mb-0">
                <label htmlFor="refusalReason">Motif du refus (visible par le client)</label>
                <textarea
                  id="refusalReason"
                  className="form-control"
                  rows={3}
                  maxLength={REASON_MAX}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  autoFocus
                />
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <button className="btn btn-outline-secondary" onClick={close} disabled={busy}>
              Annuler
            </button>
            <button
              className={"btn " + (dialog.type === "validate" ? "btn-dark" : "btn-danger")}
              onClick={confirm}
              disabled={busy}
            >
              {dialog.type === "validate" ? "Valider le paiement" : "Refuser le paiement"}
            </button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default PaiementsAbonnement;
